import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { EstimateSnapshot } from './types';
import { usePanelWidth } from './hooks/usePanelWidth';

interface AiAssistantContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  estimateSnapshot: EstimateSnapshot;
  setEstimateSnapshot: (snapshot: EstimateSnapshot) => void;
  // Drawer width state lives here (not inside the panel) so the app shell can
  // reserve the matching space and reflow content beside the open panel.
  panelWidth: number;
  isResizing: boolean;
  startResize: (e: React.MouseEvent) => void;
}

const noop = () => undefined;

const AiAssistantContext = createContext<AiAssistantContextValue>({
  isOpen: false,
  open: noop,
  close: noop,
  toggle: noop,
  estimateSnapshot: null,
  setEstimateSnapshot: noop,
  panelWidth: 400,
  isResizing: false,
  startResize: noop,
});

export function AiAssistantContextProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [estimateSnapshot, setEstimateSnapshot] = useState<EstimateSnapshot>(null);
  const { width, isResizing, startResize } = usePanelWidth();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo<AiAssistantContextValue>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      estimateSnapshot,
      setEstimateSnapshot,
      panelWidth: width,
      isResizing,
      startResize,
    }),
    [isOpen, open, close, toggle, estimateSnapshot, width, isResizing, startResize]
  );

  return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>;
}

export function useAiAssistant(): AiAssistantContextValue {
  return useContext(AiAssistantContext);
}
